// Define interfaces for the data structure
interface Credential {
	id: number
	transferable: boolean
	type: string
	sku: string
	title: string
	businessName: string
	authed: boolean
	claimed: boolean
	eventId: number
	authedAt: string
	authedTo: number
	subtotal: number
	images: string[]
	isLockedByTicketSwap: boolean
	parentId: number
}

interface CredentialsState {
	credentials: Credential[]
}

function convertJsonToCredential(json: any): Credential {
	return {
		id: json.id,
		transferable: json.transferable,
		type: typeof json.type === "string" ? json.type.toLowerCase() : "",
		sku: json.sku,
		title: json.title,
		businessName: json.business_name,
		authed: json.authed,
		claimed: json.claimed,
		eventId: json.event_id,
		authedAt: json.authed_at,
		authedTo: json.authed_to,
		subtotal: json.subtotal,
		images: json.images && json.images.indexOf("http") >= 0 ? JSON.parse(json.images) : [],
		isLockedByTicketSwap: json.is_locked_by_ticket_swap,
		parentId: json.parent_id,
	}
}

// Define the Pinia store
export const useCredentialStore = defineStore("credentialStore", {
	state: (): CredentialsState => ({
		credentials: [],
	}),

	getters: {
		getCredentials: (state: CredentialsState) => () => {
			return state.credentials
		},
		getCredentialByEventId: (state: CredentialsState) => (eventId: number) => {
			return state.credentials.filter((credential: any) => credential.eventId === eventId)
		},
		getEventCredentials: (state: CredentialsState) => (eventId: number) => {
			return state.credentials.filter((cred: Credential) => cred.eventId === eventId)
		},
		getEventTickets: (state: CredentialsState) => (eventId: number) => {
			return state.credentials.filter((cred: Credential) => cred.eventId === eventId && cred.type === "ticket")
		},
		getEventLodgings: (state: CredentialsState) => (eventId: number) => {
			return state.credentials.filter((cred: Credential) => cred.eventId === eventId && cred.type === "lodging")
		},
		getEventUpgrades: (state: CredentialsState) => (eventId: number) => {
			//Group all upgrades together and explicitly define them so that we don't accidentally show something.
			return state.credentials.filter(
				(cred: Credential) =>
					cred.eventId === eventId &&
					(cred.type === "access" ||
						cred.type === "decal" ||
						cred.type === "addon" ||
						cred.type === "merch" ||
						cred.type === "transportation"),
			)
		},
		getCredentialChildren: (state: CredentialsState) => (credId: number) => {
			return state.credentials.filter((credential: any) => credential.parentId === credId)
		},
		getAllCredentialChildren: (state: CredentialsState) => {
			// Recursive function to get all descendants of a credential
			const getAllChildren = (credId: number): Credential[] => {
				const children = state.credentials.filter((credential) => credential.parentId === credId)
				let allChildren: Credential[] = []
				for (const child of children) {
					allChildren.push(child)
					allChildren = allChildren.concat(getAllChildren(child.id))
				}
				return allChildren
			}
			return getAllChildren
		},
	},

	actions: {
		storeCredentialsFromJson(credentials: any) {
			credentials.forEach((cred: any) => {
				this.credentials.push(convertJsonToCredential(cred))
			})
		},
		clear() {
			this.credentials = []
		},
	},

	persist: {
		key: "up_credentialStore", // Make sure this key is unique
		storage: localStorage, // or cookies if you're using cookie storage
	},
})
